import { useMemo } from "react";
import { RemoteParticipant } from "livekit-client";

interface UseScreenShareProps {
  isScreenSharing: boolean;
  localParticipantName: string;
  remoteParticipants: RemoteParticipant[];
}

interface UseScreenShareReturn {
  isBannerVisible: boolean;
  bannerPresenterName: string;
  isSomeoneElseSharing: boolean;
  remoteScreenSharer: RemoteParticipant | undefined;
}

/**
 * Custom hook for managing screen share state.
 * Encapsulates screen share detection and presenter information logic.
 */
export function useScreenShare({
  isScreenSharing,
  localParticipantName,
  remoteParticipants
}: UseScreenShareProps): UseScreenShareReturn {
  return useMemo(() => {
    // Determine active screen sharer (local or remote)
    const remoteScreenSharer = remoteParticipants.find((p) => p.isScreenShareEnabled);
    const isSomeoneElseSharing = !!remoteScreenSharer;

    // Banner state
    const isBannerVisible = isScreenSharing || isSomeoneElseSharing;
    const bannerPresenterName = isScreenSharing
      ? localParticipantName
      : remoteScreenSharer?.name || remoteScreenSharer?.identity || "Unknown";

    return {
      isBannerVisible,
      bannerPresenterName,
      isSomeoneElseSharing,
      remoteScreenSharer
    };
  }, [isScreenSharing, localParticipantName, remoteParticipants]);
}
